import { t } from "@lingui/macro";
import { useRef, useState } from "react";
import { Helmet } from "react-helmet";

import { AccountPanel } from "./components/AccountPanel";
import { BalancesPanel } from "./components/BalancesPanel";
import { ChartPanel } from "./components/ChartPanel";
import { OrderBookPanel, type BookTab } from "./components/OrderBookPanel";
import { OrderEntryPanel, type Side } from "./components/OrderEntryPanel";
import { TerminalHeader } from "./components/TerminalHeader";
import { TickerStrip } from "./components/TickerStrip";
import { BOOK_ROWS, BOTTOM_TABS, type OrderType, type Timeframe } from "./terminalMock";
import { TERMINAL_BREAKPOINTS, useElementWidth } from "./useElementWidth";
import { useTerminalMarket } from "./useTerminalMarket";

import "./terminal.css";

const DEFAULT_LEVERAGE = 25;

type Props = {
  /** Set false to freeze the mock feed (useful for screenshots). */
  liveUpdates?: boolean;
  /** Visible order-book depth, 5–11. */
  orderBookRows?: number;
  leverage?: number;
};

export function TradeTerminal({
  liveUpdates = true,
  orderBookRows = BOOK_ROWS,
  leverage = DEFAULT_LEVERAGE,
}: Props) {
  const rootRef = useRef<HTMLDivElement>(null);
  const width = useElementWidth(rootRef);
  const bookRows = Math.min(BOOK_ROWS, Math.max(5, orderBookRows));

  const market = useTerminalMarket(liveUpdates);

  const [timeframe, setTimeframe] = useState<Timeframe>("5m");
  const [orderType, setOrderType] = useState<OrderType>("Market");
  const [side, setSide] = useState<Side>("buy");
  const [sizePercent, setSizePercent] = useState(25);
  const [bookTab, setBookTab] = useState<BookTab>("book");
  const [bottomTab, setBottomTab] = useState(BOTTOM_TABS[0]);

  const isNarrow = width < TERMINAL_BREAKPOINTS.narrow;
  const isWide = width >= TERMINAL_BREAKPOINTS.stack;
  const isThreeColumn = width >= TERMINAL_BREAKPOINTS.wide;

  /*
   * Three layout modes, driven by the terminal's own width so it also reflows
   * correctly when embedded in a narrower shell:
   *   >= 1440  chart | order entry | (book over account)
   *   940-1439 chart | order entry, with book + account spanning underneath
   *   < 940    everything stacked in one column
   */
  const gridStyle: React.CSSProperties = {
    display: "grid",
    gap: isNarrow ? 10 : 12,
    padding: isNarrow ? 8 : 12,
    alignItems: "stretch",
    gridTemplateColumns: isThreeColumn
      ? "minmax(560px,1fr) 330px 352px"
      : isWide
        ? "minmax(360px,1fr) 330px"
        : "1fr",
  };

  const rightColumnStyle: React.CSSProperties = {
    display: "flex",
    gap: isNarrow ? 10 : 12,
    minWidth: 0,
    /* row only in the middle band, where the pair sits under the two columns */
    flexDirection: isThreeColumn || !isWide ? "column" : "row",
    ...(isThreeColumn ? {} : { gridColumn: "1 / -1" }),
  };

  /*
   * A flex-basis in a column container caps the height and clips the book,
   * so the stacked modes must fall back to `0 0 auto`.
   */
  const bookFlex = isThreeColumn || !isWide ? "0 0 auto" : "1 1 320px";
  const accountFlex = isThreeColumn ? "1 1 auto" : !isWide ? "0 0 auto" : "1 1 320px";

  /*
   * The 300px floor keeps the book readable when it sits beside the account
   * card, but on a phone it is wider than the viewport — drop it there.
   */
  const stackedMinWidth = isNarrow ? 0 : 300;

  return (
    <>
      <Helmet>
        <title>{t`Trade Terminal | USDX`}</title>
        {/* Display + body faces for the terminal, loaded on this route only. */}
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
        <link
          rel="stylesheet"
          href="https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@300;400;500;600;700&family=Roboto+Flex:opsz,wght@8..144,100..900&display=swap"
        />
      </Helmet>

      <div
        ref={rootRef}
        className="usdx-terminal min-w-0 overflow-x-auto pb-24"
        style={{ minHeight: "100vh", background: "var(--t-bg)" }}
      >
        <TerminalHeader />
        <TickerStrip />

        <div style={gridStyle}>
          <div className="flex min-w-0 flex-col gap-12">
            <ChartPanel
              market={market}
              timeframe={timeframe}
              onTimeframeChange={setTimeframe}
              leverage={leverage}
              isNarrow={isNarrow}
              isWide={isWide}
            />
            <BalancesPanel activeTab={bottomTab} onTabChange={setBottomTab} />
          </div>

          <OrderEntryPanel
            price={market.price}
            leverage={leverage}
            side={side}
            onSideChange={setSide}
            orderType={orderType}
            onOrderTypeChange={setOrderType}
            sizePercent={sizePercent}
            onSizePercentChange={setSizePercent}
          />

          <div style={rightColumnStyle}>
            <OrderBookPanel
              market={market}
              rows={bookRows}
              activeTab={bookTab}
              onTabChange={setBookTab}
              style={{ flex: bookFlex, minWidth: stackedMinWidth }}
            />
            <AccountPanel style={{ flex: accountFlex, minWidth: stackedMinWidth }} />
          </div>
        </div>
      </div>
    </>
  );
}

export default TradeTerminal;
